import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { MyWalletResourcePackage } from "../../resources/MyWalletResourcePackage";
import { ButtonWithScaleAnimation } from "by/gamefactory/cityversetycoon/scenes/common/components/ButtonWithScaleAnimation";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { MyWalletsBox } from "../../components/wallets/MyWalletsBox";
import { MyWalletsBoxElement } from "../../components/wallets/MyWalletsBoxElement";
import { MyWalletsBoxElementType } from "../../components/wallets/MyWalletsBoxElementType";
import { ConnectWalletView, ConnectedWalletType } from "../../components/wallets/ConnectWalletView";
import { MyWalletInfoView } from "../../components/myWalletInfo/MyWalletInfoView";
import { Web3Model } from "by/gamefactory/web3/models/Web3Model";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import gsap from "gsap";
import { MyWalletPage } from "./MyWalletPage";
import { IMyWalletController } from "../../controllers/IMyWalletController";
import { FullscreenInputBlocker } from "../../../../game/components/FullscreenInputBlocker";
import { Event } from "event-system";

export class WalletPage extends MyWalletPage {
    public readonly onCloseButtonClick: Event<void>;

    private readonly pageCompletionSource: PromiseCompletionSource<void>;
    private readonly myWalletResources: MyWalletResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly web3Model: Web3Model;
    private readonly myWalletController: IMyWalletController;
    private readonly myWalletsBox: MyWalletsBox;

    private glass: Sprite;
    private closeButton: ButtonWithScaleAnimation;
    private currentWallet: MyWalletsBoxElement;
    private notificationText: Text;
    private walletInfoView?: MyWalletInfoView;
    private connectWalletView?: ConnectWalletView;

    public constructor(
        canvasScaler: ICanvasScaler,
        pageCompletionSource: PromiseCompletionSource<void>,
        myWalletResources: MyWalletResourcePackage,
        commonResources: CommonResourcesPackage,
        web3Model: Web3Model,
        myWalletController: IMyWalletController,
        myWalletsBox: MyWalletsBox
    ) {
        super(canvasScaler);
        this.pageCompletionSource = pageCompletionSource;
        this.myWalletResources = myWalletResources;
        this.commonResources = commonResources;
        this.web3Model = web3Model;
        this.myWalletController = myWalletController;
        this.myWalletsBox = myWalletsBox;
        this.currentWallet = null;
        this.onCloseButtonClick = new Event<void>();
        this.build();
        this.myWalletsBox.onWalletClick.add(this.onWalletClickHandle, this);
        this.closeButton.onUp.add(this.onCloseButtonClickHandle, this);
    }

    protected override build(): void {
        this.glass = new Sprite(this.myWalletResources.assets_views_my_wallet_ui_wallet_glass_png);
        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.width = this.closeButton.height = 60;
        this.notificationText = new Text("WALLET ADDRESS WAS COPIED", new CopyedTextStyle());
        this.notificationText.alpha = 0;

        this.addChild(
            this.glass,
            this.closeButton,
            this.notificationText
        );
        super.build();
    }

    protected override layout(_width: number, _height: number): void {
        this.closeButton.position.set(516, 43);
        this.notificationText.position.set(120, 500);
    }

    private processCurrentWalletView(): void {
        if (this.walletInfoView) {
            this.walletInfoView.onCopyAddressButtonClick.remove(this.onCopyAddressClickHandle, this);
            this.walletInfoView.onDisconnectButtonClick?.remove(this.onDisconnectButtonClickHandle, this);
            this.walletInfoView.destroy();
            this.walletInfoView = null;
        }
        if (this.connectWalletView) {
            this.connectWalletView.onConnectWalletClick.remove(this.onConnectWalletClickHandle, this);
            this.connectWalletView.destroy();
            this.connectWalletView = null;
        }
        if (this.currentWallet.data.type === MyWalletsBoxElementType.CONNECT_WALLET) {
            this.connectWalletView = new ConnectWalletView(
                this.commonResources,
                this.myWalletResources
            )
            this.addChild(this.connectWalletView);
            this.connectWalletView.onConnectWalletClick.add(this.onConnectWalletClickHandle, this);
        } else {
            this.walletInfoView = new MyWalletInfoView(
                this.commonResources,
                this.myWalletResources,
                this.currentWallet.data.type,
                this.currentWallet.data.walletAddress,
                this.currentWallet.data.networkType,
                true
            );
            this.addChild(this.walletInfoView);
            this.walletInfoView.onCopyAddressButtonClick.add(this.onCopyAddressClickHandle, this);
            this.walletInfoView.onDisconnectButtonClick?.add(this.onDisconnectButtonClickHandle, this);
        }
    }

    private onCloseButtonClickHandle(): void {
        this.currentWallet.selected = false;
        this.currentWallet = null;
        this.myWalletsBox.hideTooltip();
        this.onCloseButtonClick.emit();
    }

    private onWalletClickHandle(wallet: MyWalletsBoxElement): void {
        if (this.currentWallet === wallet) {
            return;
        }
        if (this.currentWallet) {
            this.currentWallet.selected = false;
        }
        this.currentWallet = wallet;
        this.currentWallet.selected = true;
        this.processCurrentWalletView();
        this.myWalletsBox.hideTooltip();
    }

    private async onConnectWalletClickHandle(connectedWallet: ConnectedWalletType): Promise<void> {
        this.myWalletsBox.hideTooltip();
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        await this.myWalletController.connectWallet(connectedWallet);
        this.removeChild(inputBlocker);
    }

    private async onDisconnectButtonClickHandle(): Promise<void> {
        this.myWalletsBox.hideTooltip();
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        await this.myWalletController.disconnectWallet(this.currentWallet.data.walletAddress);
        this.removeChild(inputBlocker);
    }

    private async onCopyAddressClickHandle(walletAddress: string): Promise<void> {
        window.FixedClipboard.copy(walletAddress);
        this.showNotification();
    }

    private async showNotification(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.to(this.notificationText, { alpha: 1, duration: 0.3 });
        animationTimeLine.to(this.notificationText, { alpha: 0, duration: 0.3, delay: 1 }, ">");
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.connectWalletView?.onConnectWalletClick.remove(this.onConnectWalletClickHandle, this);
        this.walletInfoView?.onCopyAddressButtonClick.remove(this.onCopyAddressClickHandle, this);
        this.walletInfoView?.onDisconnectButtonClick?.remove(this.onDisconnectButtonClickHandle, this);
        this.myWalletsBox.onWalletClick.remove(this.onWalletClickHandle, this);
        this.closeButton.onUp.remove(this.onCloseButtonClickHandle, this);
        super.destroy(options);
    }
}

class CopyedTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#fefeff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40
        }));
    }
}
